import React from "react";
import formatValue from "../../../utils/formatValue";
import DashboardCard from "../components/DashboardCard";
import MetricInfoTooltip from "../components/MetricInfoTooltip";
import {
  NET_RENT_RECEIVED_INFO_LABEL,
  NET_RENT_RECEIVED_TOOLTIP_BODY,
  NET_RENT_RECEIVED_TOOLTIP_TITLE,
} from "../constants/netRentReceivedTooltip";
import { IDashboardAdditionalData, IKenloPaymentsSummary } from "../types/ownerDashboard.types";
import { AVAILABLE_LISTINGS_SECTION_ID } from "../utils/selectAvailableOwnerListings";
import {
  ActionsRow,
  AvatarCircle,
  CardDivider,
  HeroMetricCard,
  HeroMetricInfoAnchor,
  HeroMetricLabel,
  HeroMetricLabelRow,
  HeroMetricValue,
  MetricCard,
  MetricLabel,
  MetricsBlock,
  MetricsGrid,
  MetricValue,
  PrimaryAction,
  ProfileCard,
  ProfileLink,
  ProfileSection,
  ProfileTextGroup,
  RolePill,
  SecondaryAction,
  WelcomeTitle,
} from "./dashboardStyles";

const EMAIL_ADDRESS = "contato@7cantos.com";
const EMAIL_SUBJECT = "Tenho uma reclamação da 7cantos.";

export default function UserInfoCard({
  name,
  additionalData,
  kenloSummary,
  isShellLoading = false,
  isKenloLoading = false,
  smDown,
  mdDown = false,
  lgDown = false,
}: {
  name: string;
  additionalData: IDashboardAdditionalData;
  kenloSummary?: IKenloPaymentsSummary | null;
  isShellLoading?: boolean;
  isKenloLoading?: boolean;
  smDown: boolean;
  mdDown?: boolean;
  lgDown?: boolean;
  width?: string;
  boxSizing?: string;
}) {
  const {
    available_properties,
    rented_properties,
    schedules,
  } = additionalData;

  const ownerFirstName = name?.split(" ")[0] || "Proprietário";
  const formattedTotal = formatValue(kenloSummary?.rent_received ?? 0);
  const formattedReceivedRents = String(kenloSummary?.total_payments ?? 0).padStart(2, "0");
  const formattedAvailableProperties = String(available_properties).padStart(2, "0");
  const formattedRentedProperties = String(rented_properties).padStart(2, "0");
  const formattedSchedules = String(schedules).padStart(2, "0");
  const isHorizontal = !smDown && !mdDown;

  const secondaryMetrics: Array<{
    label: string;
    value: string;
    isKenloMetric: boolean;
    href?: string;
  }> = [
    { label: "Imóveis alugados", value: formattedRentedProperties, isKenloMetric: false },
    {
      label: "Imóveis anunciados",
      value: formattedAvailableProperties,
      isKenloMetric: false,
      href: available_properties > 0 ? `#${AVAILABLE_LISTINGS_SECTION_ID}` : undefined,
    },
    { label: "Aluguéis recebidos", value: formattedReceivedRents, isKenloMetric: true },
    { label: "Visitas agendadas", value: formattedSchedules, isKenloMetric: false },
  ];

  const shouldShowMetricSkeleton = (isKenloMetric: boolean) =>
    isShellLoading || (isKenloLoading && isKenloMetric);

  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";

  return (
    <DashboardCard padding={padding}>
      <ProfileCard $horizontal={isHorizontal}>
        <ProfileSection>
          <AvatarCircle $compact={smDown || mdDown} aria-hidden>
            {ownerFirstName?.charAt(0)}
          </AvatarCircle>
          <ProfileTextGroup>
            <WelcomeTitle>
              {isShellLoading ? (
                <span className="inline-block h-7 w-48 animate-pulse rounded-full bg-white/[0.08]" />
              ) : (
                <>Bem-vindo(a), {ownerFirstName}!</>
              )}
            </WelcomeTitle>
            <RolePill>Proprietário 7Cantos</RolePill>
            <ProfileLink to="/me">Edite seu perfil</ProfileLink>
          </ProfileTextGroup>
        </ProfileSection>

        {additionalData && (
          <MetricsBlock $horizontal={isHorizontal}>
            <HeroMetricCard $inline={isHorizontal}>
              <HeroMetricInfoAnchor>
                <MetricInfoTooltip
                  title={NET_RENT_RECEIVED_TOOLTIP_TITLE}
                  description={NET_RENT_RECEIVED_TOOLTIP_BODY}
                  ariaLabel={NET_RENT_RECEIVED_INFO_LABEL}
                />
              </HeroMetricInfoAnchor>
              <HeroMetricLabelRow>
                <HeroMetricLabel>Aluguel líquido recebido</HeroMetricLabel>
              </HeroMetricLabelRow>
              {isKenloLoading ? (
                <div className="mt-2 h-9 w-40 animate-pulse rounded-xl bg-white/[0.08]" />
              ) : (
                <HeroMetricValue>{formattedTotal}</HeroMetricValue>
              )}
            </HeroMetricCard>

            <MetricsGrid $horizontal={isHorizontal}>
              {secondaryMetrics.map((metric) => (
                <MetricCard
                  key={metric.label}
                  as={metric.href ? "a" : "div"}
                  href={metric.href}
                  $compact={isHorizontal}
                  $clickable={Boolean(metric.href)}
                >
                  <MetricLabel>{metric.label}</MetricLabel>
                  {shouldShowMetricSkeleton(metric.isKenloMetric) ? (
                    <div className="mt-1 h-5 w-14 animate-pulse rounded-lg bg-white/[0.08] sm:h-6 sm:w-16" />
                  ) : (
                    <MetricValue>{metric.value}</MetricValue>
                  )}
                </MetricCard>
              ))}
            </MetricsGrid>
          </MetricsBlock>
        )}

        <CardDivider $horizontal={isHorizontal} />

        <ActionsRow $horizontal={isHorizontal}>
          <PrimaryAction href="https://api.whatsapp.com/send/?phone=551140037654&text&type=phone_number&app_absent=0">
            Suporte 7Cantos
          </PrimaryAction>
          <SecondaryAction
            href={`mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`}
          >
            Reclamações
          </SecondaryAction>
        </ActionsRow>
      </ProfileCard>
    </DashboardCard>
  );
}
